import React, { useState, useEffect, useRef } from 'react';
import {
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
  IconTrophy, IconEdit, IconDeviceFloppy, IconX, IconPlus, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import api from '../api/api';
import html2canvas from 'html2canvas';
import { ScratchSettingsLayer, calculateScratch, ScratchResultTable } from './ScratchBetting';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

/** 이미지 파일을 지정된 최대 변 길이(px)에 맞춰 비율 유지하며 JPEG로 리사이즈 */
function resizeImageFile(file, maxSize, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const scale = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error('이미지 변환 실패'));
      }, 'image/jpeg', quality);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

const WEATHER_OPTIONS = [
  { code: 'sunny',  label: '맑음',   Icon: IconSun,        color: '#f59e0b' },
  { code: 'cloudy', label: '흐림',   Icon: IconCloud,      color: '#6b7280' },
  { code: 'rain',   label: '비',     Icon: IconCloudRain,  color: '#3b82f6' },
  { code: 'snow',   label: '눈',     Icon: IconSnowflake,  color: '#60a5fa' },
  { code: 'wind',   label: '바람',   Icon: IconWind,       color: '#8b5cf6' },
  { code: 'storm',  label: '폭풍',   Icon: IconCloudStorm, color: '#ef4444' },
];

const STATUS_MAP = { PENDING: '대기', CANCELLED: '취소', COMPLETED: '완료' };

const WeatherIcon = ({ code, size = 16 }) => {
  const w = WEATHER_OPTIONS.find(o => o.code === code);
  if (!w) return null;
  return <w.Icon size={size} color={w.color} title={w.label} />;
};

const parsePar = (parData) => {
  if (!parData) return [];
  try { return JSON.parse(parData); } catch {
    return parData.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
  }
};

const parseHoles = (jsonStr, key) => {
  if (!jsonStr) return {};
  try {
    const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
    return obj[key] || {};
  } catch { return {}; }
};

const ScoreCell = ({ value, par }) => {
  if (value === '' || value == null) return <span style={{ color: '#ccc' }}>-</span>;
  const v = Number(value);
  if (isNaN(v)) return <span style={{ color: '#ccc' }}>-</span>;
  const isDoublePar = par > 0 && v === par;
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 18, height: 18, fontSize: 10, lineHeight: 1, textAlign: 'center',
  };
  if (v <= -2) return <span style={{ ...base, border: 'double 3px #dd4b39', borderRadius: '50%', fontWeight: 'bold', color: '#dd4b39' }}>{v}</span>;
  if (v === -1) return <span style={{ ...base, border: 'solid 1px #dd4b39', borderRadius: '50%', color: '#dd4b39' }}>{v}</span>;
  if (v === 0) return <span style={{ ...base }}>{v}</span>;
  if (isDoublePar) return <span style={{ ...base, background: '#555', color: '#fff' }}>{v}</span>;
  if (v === 1) return <span style={{ ...base, border: 'solid 1px #337ab7', color: '#337ab7' }}>{v}</span>;
  if (v === 2) return <span style={{ ...base, border: 'double 3px #337ab7', color: '#337ab7' }}>{v}</span>;
  return <span style={{ ...base, background: '#ccc' }}>{v}</span>;
};

export default function ScoreCardContent({ reservation, scores, loading, saving, onSave, onReservationSave, initialEdit, onDelete, showReset, onPhotosChange }) {
  const scoreCardRef = useRef(null);
  const scratchRef = useRef(null);
  const [editing, setEditing] = useState(!!initialEdit);
  const [photos, setPhotos] = useState(reservation.photos || []);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [editScores, setEditScores] = useState([]);
  const [holeEditModal, setHoleEditModal] = useState(null); // { secIdx, holeIdx }
  const [holeEditScores, setHoleEditScores] = useState([]); // 홀 입력 레이어 전용 임시 스코어
  const [playerEditModal, setPlayerEditModal] = useState(null); // { slotIdx }
  const [playerEditScores, setPlayerEditScores] = useState([]); // 개인 전체홀 입력 전용 임시 스코어
  const [manualScores, setManualScores] = useState({}); // 직접 입력 총점 {[playerSlot]: value}
  const [scratchSettingsOpen, setScratchSettingsOpen] = useState(false);
  const [scratchResultOpen, setScratchResultOpen] = useState(false);
  const [scratchData, setScratchData] = useState(null);
  const [scratchConfig, setScratchConfig] = useState(() => {
    try { return reservation.bettingConfigJson ? JSON.parse(reservation.bettingConfigJson) : null; } catch { return null; }
  });

  // 예약 정보 로컬 state
  const [localDate, setLocalDate] = useState(reservation.reservedDate || '');
  const [localTime, setLocalTime] = useState(reservation.reservedTime?.slice(0, 5) || '');
  const [localWeather, setLocalWeather] = useState(reservation.weather || '');
  const [localTemp, setLocalTemp] = useState(reservation.temperature || '');
  const [localHoles, setLocalHoles] = useState(reservation.totalHoles || '');
  const [localGreenFee, setLocalGreenFee] = useState(reservation.greenFee ?? '');
  const [localMemo, setLocalMemo] = useState(reservation.memo || '');
  const [localGameType, setLocalGameType] = useState(reservation.gameType || '일반');
  const [localUseYn, setLocalUseYn] = useState(reservation.useYn || 'Y');
  const [localStatus, setLocalStatus] = useState(reservation.status || 'PENDING');

  // 골프장/코스
  const [courseList, setCourseList] = useState([]);
  const [localCourseId, setLocalCourseId] = useState(reservation.golfCourseId || '');
  const [localCourseName, setLocalCourseName] = useState(reservation.golfCourseName || '');
  const [courseInput, setCourseInput] = useState(reservation.golfCourseName || '');
  const [showCourseList, setShowCourseList] = useState(false);
  const courseDropRef = useRef(null);
  const [sectionList, setSectionList] = useState([]);
  const [localSections, setLocalSections] = useState(
    (reservation.sections || []).map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1, sectionName: s.sectionName, holesCount: s.holesCount, parData: s.parData }))
  );

  // 멤버
  const [members, setMembers] = useState([]);
  const [addMemberId, setAddMemberId] = useState('');

  useEffect(() => {
    api.get('/golf/members').then(res => setMembers(res.data || [])).catch(() => {});
  }, []);

  // 기존 내기 설정이 있으면 자동 계산
  useEffect(() => {
    if (scratchConfig && scores.length > 0 && sections.length > 0) {
      const data = calculateScratch(scratchConfig, scores, sections);
      setScratchData(data);
    }
  }, [scores, scratchConfig]); // eslint-disable-line

  useEffect(() => {
    if (editing && courseList.length === 0) {
      api.get('/golf/courses').then(res => setCourseList(res.data?.content || res.data || [])).catch(() => {});
    }
  }, [editing, courseList.length]);

  useEffect(() => {
    if (editing && localCourseId) {
      api.get(`/golf/courses/${localCourseId}/sections`).then(res => setSectionList(res.data || [])).catch(() => setSectionList([]));
    }
  }, [editing, localCourseId]);

  useEffect(() => {
    const handler = (e) => { if (courseDropRef.current && !courseDropRef.current.contains(e.target)) setShowCourseList(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 코스 변경 시 홀 수 자동 계산
  useEffect(() => {
    if (!editing) return;
    if (localSections.length === 0) return;
    const h0 = localSections[0]?.holesCount || 9;
    if (localSections.length === 1) { setLocalHoles(String(h0)); return; }
    const h1 = localSections[1]?.holesCount || 9;
    if (localSections[0]?.sectionId === localSections[1]?.sectionId) setLocalHoles(String(h0 * 2));
    else setLocalHoles(String(h0 + h1));
  }, [editing, localSections]);

  const filteredCourses = courseInput
    ? courseList.filter(c => c.name.toLowerCase().includes(courseInput.toLowerCase())).slice(0, 30)
    : courseList.slice(0, 30);

  const selectCourse = (c) => {
    setCourseInput(c.name);
    setLocalCourseId(c.id);
    setLocalCourseName(c.name);
    setShowCourseList(false);
    setLocalSections([]);
  };

  const toggleGameType = () => setLocalGameType(prev => prev === '메이저' ? '일반' : '메이저');
  const toggleUseYn = () => setLocalUseYn(prev => prev === 'Y' ? 'N' : 'Y');

  const openPlayerEdit = (slotIdx) => {
    if (editing) {
      setPlayerEditScores(JSON.parse(JSON.stringify(editScores)));
    } else {
      setPlayerEditScores(scores.length > 0 ? JSON.parse(JSON.stringify(scores)) : []);
    }
    setPlayerEditModal({ slotIdx });
  };

  const openHoleEdit = (secIdx, holeIdx) => {
    // 편집 모드 진입 없이 임시 스코어로 레이어 오픈
    if (editing) {
      setHoleEditScores(JSON.parse(JSON.stringify(editScores)));
    } else {
      setHoleEditScores(scores.length > 0 ? JSON.parse(JSON.stringify(scores)) : []);
    }
    setHoleEditModal({ secIdx, holeIdx });
  };

  const startEdit = () => {
    const initScores = scores.length > 0 ? JSON.parse(JSON.stringify(scores)) : [];
    setEditScores(initScores);
    const initManual = {};
    initScores.forEach(s => { if (s.totalScore != null) initManual[s.playerSlot] = s.totalScore; });
    setManualScores(initManual);
    setEditing(true);
  };

  const handleResetScores = () => {
    if (!window.confirm('모든 스코어를 초기화하시겠습니까?')) return;
    setEditScores(prev => prev.map(s => ({ ...s, holeScoresJson: null, totalScore: 0, totalPutting: 0 })));
    setManualScores({});
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditScores([]);
    setManualScores({});
    setLocalDate(reservation.reservedDate || '');
    setLocalTime(reservation.reservedTime?.slice(0, 5) || '');
    setLocalWeather(reservation.weather || '');
    setLocalTemp(reservation.temperature || '');
    setLocalHoles(reservation.totalHoles || '');
    setLocalGreenFee(reservation.greenFee ?? '');
    setLocalMemo(reservation.memo || '');
    setLocalGameType(reservation.gameType || '일반');
    setLocalUseYn(reservation.useYn || 'Y');
    setLocalStatus(reservation.status || 'PENDING');
    setLocalCourseId(reservation.golfCourseId || '');
    setLocalCourseName(reservation.golfCourseName || '');
    setCourseInput(reservation.golfCourseName || '');
    setLocalSections((reservation.sections || []).map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1, sectionName: s.sectionName, holesCount: s.holesCount, parData: s.parData })));
  };

  /** 사진 추가: 클라이언트에서 400px(원본)/80px(썸네일)로 리사이즈 후 업로드 */
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || !reservation.id) return;

    const remaining = 6 - (photos.length || 0);
    if (files.length > remaining) {
      alert(`사진은 최대 6장까지 등록할 수 있습니다. (${remaining}장 추가 가능)`);
      return;
    }

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) {
        const [fullBlob, thumbBlob] = await Promise.all([
          resizeImageFile(file, 400),
          resizeImageFile(file, 80),
        ]);
        const baseName = (file.name || 'photo').replace(/\.[^./\\]+$/, '');
        formData.append('full', fullBlob, `${baseName}.jpg`);
        formData.append('thumb', thumbBlob, `${baseName}_thumb.jpg`);
      }
      const res = await api.post(`/golf/reservations/${reservation.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPhotos = res.data;
      setPhotos(prev => {
        const updated = [...prev, ...newPhotos];
        onPhotosChange?.(updated);
        return updated;
      });
    } catch (err) {
      alert(err?.response?.data?.error || '사진 업로드에 실패했습니다.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('사진을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/golf/reservations/${reservation.id}/photos/${photoId}`);
      setPhotos(prev => {
        const updated = prev.filter(p => p.id !== photoId);
        onPhotosChange?.(updated);
        return updated;
      });
    } catch {
      alert('사진 삭제에 실패했습니다.');
    }
  };

  const handleSave = async () => {
    const secs = localSections || [];
    const updated = editScores.map(s => {
      let json = {};
      try {
        if (s.holeScoresJson) {
          const parsed = typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : s.holeScoresJson;
          json = parsed || {};
        }
      } catch { json = {}; }
      let totalOver = 0, totalPutting = 0, totalPar = 0;
      secs.forEach((sec, idx) => {
        const pars = parsePar(sec.parData);
        const holeData = json[`no${idx + 1}`] || {};
        const puttData = json[`putting${idx + 1}`] || {};
        for (let h = 1; h <= (sec.holesCount || 9); h++) {
          totalOver += Number(holeData[String(h)] || 0);
          totalPar += (pars[h - 1] || 0);
          totalPutting += Number(puttData[String(h)] || 0);
        }
      });
      const manualTotal = manualScores[s.playerSlot] != null && manualScores[s.playerSlot] !== '' ? Number(manualScores[s.playerSlot]) : null;
      return {
        ...s,
        totalScore: manualTotal != null ? manualTotal : totalPar + totalOver,
        overPar: manualTotal != null ? manualTotal - totalPar : totalOver,
        totalPutting,
        holeScoresJson: typeof s.holeScoresJson === 'object' ? JSON.stringify(s.holeScoresJson) : s.holeScoresJson,
      };
    });

    // 예약 정보 저장
    const payload = {
      reservedDate: localDate,
      reservedTime: localTime ? localTime + ':00' : null,
      weather: localWeather || null,
      temperature: localTemp || null,
      greenFee: localGreenFee !== '' ? localGreenFee : null,
      memo: localMemo || null,
      gameType: localGameType,
      useYn: localUseYn,
      status: localStatus,
    };
    if (localCourseId) payload.golfCourseId = Number(localCourseId);
    if (localSections.length > 0) payload.sections = localSections.map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1 }));

    if (onReservationSave) {
      await onReservationSave(payload);
    }

    // 스코어 저장
    onSave(updated);
    setEditing(false);
  };

  const sections = localSections;
  const displayScores = editing ? editScores : scores;
  const firstPlayer = displayScores.find(s => s.joinYn !== 'N');

  const totalCoursePar = sections.reduce((sum, sec) => {
    const pars = parsePar(sec.parData);
    return sum + pars.reduce((a, b) => a + b, 0);
  }, 0);

  const inputStyle = { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' };
  const badgeStyle = (bg) => ({ background: bg, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 });

  const handleImageSave = async (ref, filename, radius = 24) => {
    if (!ref.current) return;
    try {
      const raw = await html2canvas(ref.current, { backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
      if (radius > 0) {
        const canvas = document.createElement('canvas');
        canvas.width = raw.width; canvas.height = raw.height;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(radius, 0); ctx.lineTo(raw.width - radius, 0);
        ctx.quadraticCurveTo(raw.width, 0, raw.width, radius);
        ctx.lineTo(raw.width, raw.height - radius);
        ctx.quadraticCurveTo(raw.width, raw.height, raw.width - radius, raw.height);
        ctx.lineTo(radius, raw.height);
        ctx.quadraticCurveTo(0, raw.height, 0, raw.height - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
        ctx.closePath(); ctx.clip();
        ctx.drawImage(raw, 0, 0);
        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        const link = document.createElement('a');
        link.download = filename;
        link.href = raw.toDataURL('image/png');
        link.click();
      }
    } catch (err) { console.error('이미지 저장 실패:', err); }
  };

  return (
    <>
      <div ref={scoreCardRef}>
      {/* 헤더 */}
      <div style={{
        background: 'linear-gradient(135deg, #1a5276 0%, #2980b9 100%)',
        color: '#fff', padding: '16px 20px',
      }}>
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1 }}>
            {editing ? (
              <div>
                {/* 1행: 골프장 */}
                <div ref={courseDropRef} style={{ position: 'relative', marginBottom: 6 }}>
                  <input type="text" value={courseInput} autoComplete="off" placeholder="골프장 검색"
                    onChange={e => { setCourseInput(e.target.value); setShowCourseList(true); if (!e.target.value) { setLocalCourseId(''); setLocalCourseName(''); setSectionList([]); setLocalSections([]); } }}
                    onFocus={() => setShowCourseList(true)}
                    style={{ ...inputStyle, fontSize: 14, fontWeight: 700, width: '100%' }} />
                  {showCourseList && filteredCourses.length > 0 && (
                    <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 200, overflowY: 'auto', margin: 0, padding: 0, listStyle: 'none', background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      {filteredCourses.map(c => (
                        <li key={c.id} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#333', background: String(localCourseId) === String(c.id) ? '#e8f4fd' : undefined }}
                          onMouseDown={() => selectCourse(c)}
                          onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                          onMouseLeave={e => e.currentTarget.style.background = String(localCourseId) === String(c.id) ? '#e8f4fd' : ''}>
                          {c.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* 2행: 전반 / 후반 */}
                {sectionList.length > 0 && (
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: 12 }}>
                    <span style={{ opacity: 0.7, fontWeight: 600 }}>전반</span>
                    <select value={localSections[0]?.sectionId || ''} onChange={e => {
                      const sec = sectionList.find(s => String(s.id) === e.target.value);
                      setLocalSections(prev => { const second = prev[1] || null; if (sec) { const first = { sectionId: sec.id, playOrder: 1, sectionName: sec.name, holesCount: sec.holesCount, parData: sec.parData }; return second ? [first, second] : [first]; } return second ? [second] : []; });
                    }} style={inputStyle}>
                      <option value="" style={{ color: '#333' }}>선택</option>
                      {sectionList.filter(s => s.useYn !== 'N').map(s => <option key={s.id} value={s.id} style={{ color: '#333' }}>{s.name}</option>)}
                    </select>
                    <span style={{ opacity: 0.7, fontWeight: 600 }}>후반</span>
                    <select value={localSections[1]?.sectionId || ''} onChange={e => {
                      const sec = sectionList.find(s => String(s.id) === e.target.value);
                      setLocalSections(prev => { const first = prev[0] || null; if (sec) { const second = { sectionId: sec.id, playOrder: 2, sectionName: sec.name, holesCount: sec.holesCount, parData: sec.parData }; return first ? [first, second] : [second]; } return first ? [first] : []; });
                    }} style={inputStyle}>
                      <option value="" style={{ color: '#333' }}>선택</option>
                      {sectionList.filter(s => s.useYn !== 'N').map(s => <option key={s.id} value={s.id} style={{ color: '#333' }}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {/* 3행: 날짜 / 시간 / 그린피 */}
                <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: 12 }}>
                  <input type="date" value={localDate} onChange={e => setLocalDate(e.target.value)} style={inputStyle} />
                  <input type="time" value={localTime} onChange={e => setLocalTime(e.target.value)} style={{ ...inputStyle, width: 90 }} />
                  <input type="text" value={localGreenFee} onChange={e => setLocalGreenFee(e.target.value)} placeholder="그린피" style={{ ...inputStyle, width: 65, textAlign: 'center' }} />
                  <span style={{ fontSize: 11, opacity: 0.7 }}>만원</span>
                  <input type="text" value={localMemo} onChange={e => setLocalMemo(e.target.value)} placeholder="메모" style={{ ...inputStyle, width: 120 }} />
                </div>
                {/* 4행: 날씨 / 기온 / 홀 / 일반메이저 / 유효무효 / 상태 */}
                <div className="d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                  <select value={localWeather} onChange={e => setLocalWeather(e.target.value)} style={inputStyle}>
                    <option value="" style={{ color: '#333' }}>날씨</option>
                    {WEATHER_OPTIONS.map(w => <option key={w.code} value={w.code} style={{ color: '#333' }}>{w.label}</option>)}
                  </select>
                  <input type="text" value={localTemp} onChange={e => setLocalTemp(e.target.value)} placeholder="기온" style={{ ...inputStyle, width: 40, textAlign: 'center' }} />
                  <select value={localHoles} onChange={e => setLocalHoles(e.target.value)} style={inputStyle}>
                    <option value="" style={{ color: '#333' }}>홀</option>
                    <option value="9" style={{ color: '#333' }}>9홀</option>
                    <option value="18" style={{ color: '#333' }}>18홀</option>
                    <option value="27" style={{ color: '#333' }}>27홀</option>
                  </select>
                  <span onClick={toggleGameType} style={{ ...badgeStyle(localGameType === '메이저' ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.2)'), cursor: 'pointer' }}>{localGameType}</span>
                  <span onClick={toggleUseYn} style={{ ...badgeStyle(localUseYn === 'N' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'), cursor: 'pointer' }}>{localUseYn === 'N' ? '무효' : '유효'}</span>
                  <select value={localStatus} onChange={e => setLocalStatus(e.target.value)} style={inputStyle}>
                    <option value="PENDING" style={{ color: '#333' }}>대기</option>
                    <option value="COMPLETED" style={{ color: '#333' }}>완료</option>
                    <option value="CANCELLED" style={{ color: '#333' }}>취소</option>
                  </select>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.85 }}>
                {/* 0행: 골프장명 (코스) */}
                <div style={{ fontSize: 18, fontWeight: 700, opacity: 1 }}>
                  {localCourseName || reservation.golfCourseName}
                  {localSections.length > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7, marginLeft: 6 }}>
                      ({localSections.map(s => s.sectionName).join(' > ')})
                    </span>
                  )}
                </div>
                {/* 1행: 날짜 / 티오프 / 날씨 / 기온 */}
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span>{localDate}</span>
                  {localTime && <span>TEE OFF {localTime}</span>}
                  {localWeather && <span className="d-flex align-items-center gap-1"><WeatherIcon code={localWeather} size={14} />{localTemp && `${localTemp}°`}</span>}
                </div>
                {/* 2행: 홀 / 일반메이저 / 유효무효 / 상태 / 그린피 */}
                <div className="d-flex align-items-center gap-2 mt-1">
                  {localSections.length > 0 && (() => {
                    const h0 = localSections[0]?.holesCount || 9;
                    if (localSections.length === 1) return <span style={badgeStyle('rgba(56,189,248,0.3)')}>{h0}홀</span>;
                    const h1 = localSections[1]?.holesCount || 9;
                    if (localSections[0]?.sectionId === localSections[1]?.sectionId)
                      return <span style={badgeStyle('rgba(168,85,247,0.3)')}>{h0}x2</span>;
                    return <span style={badgeStyle('rgba(34,197,94,0.3)')}>{h0 + h1}홀</span>;
                  })()}
                  <span style={badgeStyle(localGameType === '메이저' ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.2)')}>{localGameType}</span>
                  <span style={badgeStyle(localUseYn === 'N' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)')}>{localUseYn === 'N' ? '무효' : '유효'}</span>
                  <span style={badgeStyle(localStatus === 'COMPLETED' ? 'rgba(59,130,246,0.3)' : localStatus === 'CANCELLED' ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.2)')}>{STATUS_MAP[localStatus] || localStatus}</span>
                  {localGreenFee && <span>{localGreenFee}</span>}
                  {localMemo && <span style={{ opacity: 0.75 }}>{localMemo}</span>}
                </div>
                {/* 3행: 참석자 프로필 + 스코어 요약 (불참자 제외) */}
                {displayScores.filter(s => s.joinYn !== 'N').length > 0 && (
                  <div className="d-flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                    {displayScores.filter(s => s.joinYn !== 'N').map(s => {
                      const mem = members.find(m => m.id === Number(s.userId));
                      return (
                        <div key={s.playerSlot} className="d-flex align-items-center gap-1"
                          style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '4px 8px', minWidth: 0, flex: '0 1 auto' }}>
                          {mem?.profileImage ? (
                            <img src={mem.profileImage} alt={mem?.nickname || s.playerName} style={{
                              width: 24, height: 24, borderRadius: '50%', objectFit: 'cover',
                              border: `2px solid ${mem.color || '#fff'}`, flexShrink: 0,
                            }} />
                          ) : (
                            <div style={{
                              width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                              background: mem?.bgcolor || mem?.color || 'rgba(255,255,255,0.3)',
                              color: mem?.bgcolor ? (mem?.color || '#fff') : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700,
                              border: `2px solid ${mem?.color || 'rgba(255,255,255,0.5)'}`,
                            }}>
                              {(mem?.nickname || s.playerName || `P${s.playerSlot}`)[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {mem?.nickname || s.playerName || `P${s.playerSlot}`}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {s.totalScore ?? '-'}
                              {s.overPar != null && s.overPar !== 0 && (
                                <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.8, marginLeft: 2 }}>
                                  ({s.overPar > 0 ? '+' : ''}{s.overPar})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 본문 */}
      <div style={{ overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-5 text-muted">
            <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
          </div>
        ) : (
          <>
            {sections.map((sec, secIdx) => {
              const pars = parsePar(sec.parData);
              const holes = Array.from({ length: sec.holesCount || 9 }, (_, i) => i + 1);
              const parSum = pars.reduce((a, b) => a + b, 0);
              const isLast = secIdx === sections.length - 1;

              return (
                <div key={secIdx}>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 text-center" style={{ fontSize: 11, tableLayout: 'fixed', width: '100%' }}>
                      <thead>
                        <tr style={{ background: '#2471a3', color: '#fff' }}>
                          <th style={{ width: 48, textAlign: 'left', paddingLeft: 4, fontWeight: 700, fontSize: 10, overflow: 'hidden', whiteSpace: 'nowrap' }}>{sec.sectionName}</th>
                          {holes.map(h => <th key={h} style={{ fontWeight: 700, padding: '2px 0', cursor: 'pointer' }} onClick={() => openHoleEdit(secIdx, h - 1)}>{h}</th>)}
                          <th style={{ fontWeight: 700, padding: '2px 0' }}>{sections.length > 1 ? '' : 'T'}</th>
                          {sections.length > 1 && <th style={{ fontWeight: 700, padding: '2px 0', background: isLast ? '#1a5276' : '#2471a3' }}>{isLast ? 'T' : ''}</th>}
                        </tr>
                        <tr style={{ background: '#d6eaf8' }}>
                          <td style={{ textAlign: 'left', paddingLeft: 4, fontWeight: 700, color: '#2c3e50', fontSize: 10 }}>PAR</td>
                          {holes.map((h, i) => <td key={h} style={{ fontWeight: 600, color: '#2c3e50', padding: '1px 0' }}>{pars[i] ?? '-'}</td>)}
                          <td style={{ fontWeight: 700, color: '#2c3e50', padding: '1px 0' }}>{parSum}</td>
                          {sections.length > 1 && <td style={{ fontWeight: 700, color: '#2c3e50', background: isLast ? '#aed6f1' : '#d6eaf8', padding: '1px 0' }}>{isLast ? totalCoursePar : ''}</td>}
                        </tr>
                      </thead>
                      <tbody>
                        {displayScores.filter(s => s.joinYn !== 'N').map((s) => {
                          const slotIdx = displayScores.indexOf(s);
                          const mem = members.find(m => m.id === Number(s.userId));
                          const displayName = mem?.nickname || s.playerName || `P${s.playerSlot}`;
                          const hd = parseHoles(s.holeScoresJson, `no${secIdx + 1}`);
                          const pd = parseHoles(s.holeScoresJson, `putting${secIdx + 1}`);
                          const vals = holes.map(h => hd[String(h)]);
                          const putts = holes.map(h => pd[String(h)]);
                          const secSum = vals.reduce((acc, v) => acc + (v !== '' && v != null ? Number(v) : 0), 0);
                          const secPuttSum = putts.reduce((acc, v) => acc + (v !== '' && v != null ? Number(v) : 0), 0);
                          let grandTotal = null;
                          let grandPutting = 0;
                          if (isLast && sections.length > 1) {
                            grandTotal = 0;
                            sections.forEach((ss, si) => {
                              const hh = parseHoles(s.holeScoresJson, `no${si + 1}`);
                              const pp = parseHoles(s.holeScoresJson, `putting${si + 1}`);
                              Array.from({ length: ss.holesCount || 9 }, (_, i) => i + 1).forEach(h => {
                                grandTotal += Number(hh[String(h)] || 0);
                                grandPutting += Number(pp[String(h)] || 0);
                              });
                            });
                          }
                          const secStrokeTotal = parSum + secSum;
                          const grandStrokeTotal = grandTotal != null ? totalCoursePar + grandTotal : null;

                          return (
                            <tr key={s.playerSlot} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ textAlign: 'left', paddingLeft: 4, fontWeight: 600, fontSize: 10, overflow: 'hidden', whiteSpace: 'nowrap', cursor: !editing && secIdx === 0 ? 'pointer' : undefined }}
                                onClick={() => { if (!editing && secIdx === 0) openPlayerEdit(slotIdx); }}>
                                {editing && secIdx === 0 ? (
                                  <div className="d-flex align-items-center gap-1">
                                    <span>{displayName}</span>
                                    <IconX size={12} style={{ cursor: 'pointer', color: '#d63939', opacity: 0.5, flexShrink: 0 }}
                                      onClick={() => {
                                        if (window.confirm(`${displayName}을(를) 삭제하시겠습니까?`))
                                          setEditScores(prev => prev.filter((_, i) => i !== slotIdx));
                                      }} title="삭제" />
                                  </div>
                                ) : (
                                  displayName
                                )}
                              </td>
                              {vals.map((v, i) => {
                                const p = putts[i];
                                const hasPutt = p !== '' && p != null && Number(p) > 0;
                                return (
                                  <td key={i} style={{ padding: '1px 0', verticalAlign: 'middle', cursor: 'pointer' }}
                                    onClick={() => openHoleEdit(secIdx, i)}>
                                    <ScoreCell value={v} par={pars[i] || 0} />
                                    {hasPutt && <div style={{ fontSize: 8, color: '#888', lineHeight: 1, marginTop: 0 }}>{p}</div>}
                                  </td>
                                );
                              })}
                              <td style={{ fontWeight: 700, verticalAlign: 'middle', padding: '1px 0' }}>
                                {editing && sections.length === 1 ? (
                                  <input
                                    type="number"
                                    value={manualScores[s.playerSlot] ?? secStrokeTotal ?? ''}
                                    onChange={e => setManualScores(prev => ({ ...prev, [s.playerSlot]: e.target.value }))}
                                    style={{ width: 44, textAlign: 'center', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 3, fontSize: 11, fontWeight: 700, padding: '1px 2px', color: '#333' }}
                                  />
                                ) : (
                                  <>
                                    <div>{(sections.length === 1 ? (s.totalScore ?? secStrokeTotal) : secStrokeTotal) || '-'}</div>
                                    {(() => { const ov = sections.length === 1 ? (secSum !== 0 ? secSum : s.overPar) : secSum; return ov != null && ov !== 0 && <div style={{ fontSize: 9, color: ov > 0 ? '#e03131' : '#2fb344' }}>({ov > 0 ? '+' : ''}{ov})</div>; })()}
                                    {secPuttSum > 0 && <div style={{ fontSize: 8, color: '#888' }}>P{secPuttSum}</div>}
                                  </>
                                )}
                              </td>
                              {sections.length > 1 && (
                                <td style={{ fontWeight: 800, fontSize: 12, background: '#f8f9fa', verticalAlign: 'middle', padding: '1px 0' }}>
                                  {isLast ? (
                                    editing ? (
                                      <input
                                        type="number"
                                        value={manualScores[s.playerSlot] ?? grandStrokeTotal ?? ''}
                                        onChange={e => setManualScores(prev => ({ ...prev, [s.playerSlot]: e.target.value }))}
                                        style={{ width: 44, textAlign: 'center', background: 'rgba(0,0,0,0.07)', border: '1px solid #bbb', borderRadius: 3, fontSize: 11, fontWeight: 700, padding: '1px 2px', color: '#333' }}
                                      />
                                    ) : (
                                      <>
                                        <div>{(s.totalScore ?? grandStrokeTotal) || '-'}</div>
                                        {(() => { const ov = grandTotal !== 0 ? grandTotal : s.overPar; return ov != null && ov !== 0 && <div style={{ fontSize: 9, fontWeight: 600, color: ov > 0 ? '#e03131' : '#2fb344' }}>({ov > 0 ? '+' : ''}{ov})</div>; })()}
                                        {grandPutting > 0 && <div style={{ fontSize: 8, color: '#888' }}>P{grandPutting}</div>}
                                      </>
                                    )
                                  ) : ''}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {/* 편집: 인원추가 */}
            {editing && (
              <div className="px-3 py-2 d-flex align-items-center gap-2 border-top" style={{ background: '#f8f9fa' }}>
                <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 140 }}
                  value={addMemberId} onChange={e => setAddMemberId(e.target.value)}>
                  <option value="">멤버 선택</option>
                  {members.filter(m => !editScores.some(s => s.userId && Number(s.userId) === m.id && s.joinYn !== 'N')).map(m => (
                    <option key={m.id} value={m.id}>{m.nickname || m.name}</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                  disabled={!addMemberId}
                  onClick={() => {
                    const mem = members.find(m => m.id === Number(addMemberId));
                    if (!mem) return;
                    const maxSlot = editScores.reduce((mx, s) => Math.max(mx, s.playerSlot || 0), 0);
                    setEditScores(prev => [...prev, {
                      playerSlot: maxSlot + 1, playerName: mem.nickname || mem.name, userId: mem.id,
                      joinYn: 'Y', totalScore: null, overPar: null, totalPutting: null,
                      holeScoresJson: null, memo: '',
                    }]);
                    setAddMemberId('');
                  }}>
                  <IconPlus size={14} /> 인원추가
                </button>
              </div>
            )}

            {!editing && scores.length === 0 && (
              <div className="text-center py-5 text-muted" style={{ fontSize: 14 }}>
                <IconTrophy size={36} className="mb-2" style={{ opacity: 0.3 }} />
                <div>스코어 데이터가 없습니다.</div>
              </div>
            )}
          </>
        )}
      </div>

      </div>{/* scoreCardRef 끝 */}

      {/* 사진 (스코어카드 이미지 저장 영역 밖) */}
      {reservation.id && (photos.length > 0 || editing) && (
        <div className="px-3 py-2 border-top">
          <label className="form-label fw-semibold mb-2">사진 {photos.length > 0 && `(${photos.length}/6)`}</label>
          <div className="d-flex flex-wrap gap-2">
            {photos.map(p => (
              <div key={p.id} className="position-relative" style={{ width: 72, height: 72 }}>
                <img src={`${api.defaults.baseURL}${p.thumbUrl}`} alt=""
                  onClick={() => setLightboxUrl(`${api.defaults.baseURL}${p.url}`)}
                  className="border rounded"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
                {editing && (
                  <button type="button"
                    className="btn btn-sm btn-danger position-absolute p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 20, height: 20, top: -6, right: -6, borderRadius: '50%' }}
                    onClick={() => handleDeletePhoto(p.id)}>
                    <IconX size={12} />
                  </button>
                )}
              </div>
            ))}
            {editing && photos.length < 6 && (
              <label className="d-flex align-items-center justify-content-center border rounded text-muted mb-0"
                style={{
                  width: 72, height: 72,
                  cursor: photoUploading ? 'default' : 'pointer',
                  opacity: photoUploading ? 0.5 : 1,
                }}>
                {photoUploading
                  ? <span className="spinner-border spinner-border-sm" />
                  : <IconPlus size={22} />}
                <input type="file" accept="image/*" multiple hidden
                  disabled={photoUploading} onChange={handlePhotoSelect} />
              </label>
            )}
          </div>
        </div>
      )}

      {/* 사진 라이트박스 */}
      {lightboxUrl && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.85)', zIndex: 2000, cursor: 'zoom-out' }}
          onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
        </div>
      )}

      {/* 하단 버튼 */}
      <div className="d-flex justify-content-between gap-2 p-2 border-top">
        {editing && onDelete ? (
          <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
            onClick={() => { if (window.confirm('예약을 삭제하시겠습니까?')) onDelete(); }}
            disabled={saving}>
            <IconTrash size={14} /> 삭제
          </button>
        ) : !editing ? (
          <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
            onClick={() => handleImageSave(scoreCardRef, `${reservation.reservedDate}_${reservation.golfCourseName || ''}_스코어카드.png`)}
            disabled={loading || scores.length === 0}>
            <IconTrophy size={14} /> 이미지 저장
          </button>
        ) : null}
        <div className="d-flex gap-2 ms-auto">
        {editing ? (
          <>
            {showReset && (
              <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                onClick={handleResetScores} disabled={saving}>
                <IconRefresh size={14} /> 리셋
              </button>
            )}
            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={cancelEdit} disabled={saving}>
              <IconX size={14} /> 취소
            </button>
            <button className="btn btn-sm btn-warning d-flex align-items-center gap-1"
              onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm" /> : <IconDeviceFloppy size={14} />}
              저장
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
              onClick={() => scratchData ? setScratchResultOpen(true) : setScratchSettingsOpen(true)}>
              <IconTrophy size={14} /> 스크라치
            </button>
            <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={startEdit}>
              <IconEdit size={14} /> 편집
            </button>
          </>
        )}
        </div>
      </div>

      {/* 홀별 입력 레이어 */}
      {holeEditModal && <HoleEditLayer
        holeEditModal={holeEditModal}
        sections={sections}
        editScores={holeEditScores}
        setEditScores={setHoleEditScores}
        members={members}
        onClose={() => setHoleEditModal(null)}
        onSaveHole={() => {
          // 홀 입력 저장: 편집 모드 중이면 editScores에 반영, 아니면 직접 서버 저장
          const secs = sections || [];
          const _si = holeEditModal.secIdx;
          const _hn = String(holeEditModal.holeIdx + 1);
          const updated = holeEditScores.map(s => {
            let json = {};
            try {
              if (s.holeScoresJson) {
                const parsed = typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : s.holeScoresJson;
                json = parsed || {};
              }
            } catch { json = {}; }
            // 현재 편집 홀: 빈 점수→0(par), 리셋('')→제거
            const _sk = `no${_si + 1}`;
            if (!json[_sk]) json[_sk] = {};
            if (json[_sk][_hn] == null) json[_sk][_hn] = '0';
            else if (json[_sk][_hn] === '') delete json[_sk][_hn];
            const _pk = `putting${_si + 1}`;
            if (json[_pk] && json[_pk][_hn] === '') delete json[_pk][_hn];
            let totalOver = 0, totalPutting = 0, totalPar = 0;
            secs.forEach((sec, idx) => {
              const ps = parsePar(sec.parData);
              const holeData = json[`no${idx + 1}`] || {};
              const puttData = json[`putting${idx + 1}`] || {};
              for (let h = 1; h <= (sec.holesCount || 9); h++) {
                totalOver += Number(holeData[String(h)] || 0);
                totalPar += (ps[h - 1] || 0);
                totalPutting += Number(puttData[String(h)] || 0);
              }
            });
            return {
              ...s,
              totalScore: totalPar + totalOver,
              overPar: totalOver,
              totalPutting,
              holeScoresJson: JSON.stringify(json),
            };
          });
          if (editing) {
            setEditScores(updated);
          } else {
            onSave(updated);
          }
          setHoleEditModal(null);
        }}
      />}

      {/* 개인 전체홀 입력 레이어 */}
      {playerEditModal && <PlayerEditLayer
        playerEditModal={playerEditModal}
        sections={sections}
        editScores={playerEditScores}
        setEditScores={setPlayerEditScores}
        members={members}
        onClose={() => setPlayerEditModal(null)}
        onSavePlayer={() => {
          const secs = sections || [];
          const updated = playerEditScores.map(s => {
            let json = {};
            try {
              if (s.holeScoresJson) {
                const parsed = typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : s.holeScoresJson;
                json = parsed || {};
              }
            } catch { json = {}; }
            // 빈 점수→0(par), 리셋('')→제거
            secs.forEach((sec, idx) => {
              const sk = `no${idx + 1}`;
              if (!json[sk]) json[sk] = {};
              const pk = `putting${idx + 1}`;
              for (let h = 1; h <= (sec.holesCount || 9); h++) {
                const hk = String(h);
                if (json[sk][hk] == null) json[sk][hk] = '0';
                else if (json[sk][hk] === '') delete json[sk][hk];
                if (json[pk] && json[pk][hk] === '') delete json[pk][hk];
              }
            });
            let totalOver = 0, totalPutting = 0, totalPar = 0;
            secs.forEach((sec, idx) => {
              const ps = parsePar(sec.parData);
              const holeData = json[`no${idx + 1}`] || {};
              const puttData = json[`putting${idx + 1}`] || {};
              for (let h = 1; h <= (sec.holesCount || 9); h++) {
                totalOver += Number(holeData[String(h)] || 0);
                totalPar += (ps[h - 1] || 0);
                totalPutting += Number(puttData[String(h)] || 0);
              }
            });
            return {
              ...s,
              totalScore: (s._manualScore != null) ? s._manualScore : totalPar + totalOver,
              overPar: (s._manualScore != null) ? (s._manualScore - totalPar) : totalOver,
              totalPutting: (s._manualPutting != null) ? s._manualPutting : totalPutting,
              holeScoresJson: JSON.stringify(json),
            };
          });
          // _manual 필드 제거
          const cleaned = updated.map(s => { const { _manualScore, _manualPutting, _manualHandy, ...rest } = s; return rest; });
          if (editing) {
            setEditScores(cleaned);
          } else {
            onSave(cleaned);
          }
          setPlayerEditModal(null);
        }}
      />}

      {/* 스크라치 설정 레이어 */}
      {/* 스크라치 결과 레이어 */}
      {scratchResultOpen && scratchData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setScratchResultOpen(false))}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 500, maxHeight: '85vh', overflow: 'auto' }}>
            <div ref={scratchRef}>
              <ScratchResultTable scratchData={scratchData} members={members} scores={scores} />
            </div>
            <div className="d-flex justify-content-between p-2 border-top">
              <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                onClick={() => handleImageSave(scratchRef, `${reservation.reservedDate}_${reservation.golfCourseName || ''}_스크라치.png`)}>
                <IconTrophy size={14} /> 이미지 저장
              </button>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={() => { setScratchResultOpen(false); setScratchSettingsOpen(true); }}>
                  <IconEdit size={14} /> 설정
                </button>
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setScratchResultOpen(false)}>
                  <IconX size={14} /> 닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {scratchSettingsOpen && (
        <ScratchSettingsLayer
          reservation={reservation}
          scores={scores}
          members={members}
          existingConfig={scratchConfig}
          onClose={() => setScratchSettingsOpen(false)}
          onConfirm={(cfg) => {
            setScratchConfig(cfg);
            const data = calculateScratch(cfg, scores, sections);
            setScratchData(data);
            setScratchSettingsOpen(false);
            setScratchResultOpen(true);
            // 설정 저장
            if (onReservationSave) {
              onReservationSave({ bettingConfigJson: JSON.stringify(cfg) });
            }
            // money 결과 저장
            if (data && onSave) {
              const updated = scores.map(s => {
                const fm = data.finalMoney[s.playerSlot];
                return fm != null ? { ...s, money: fm } : s;
              });
              onSave(updated);
            }
          }}
        />
      )}
    </>
  );
}

// 홀별 입력 레이어 컴포넌트
function HoleEditLayer({ holeEditModal, sections, editScores, setEditScores, members, onClose, onSaveHole }) {
  const { secIdx, holeIdx } = holeEditModal;
  const sec = sections[secIdx];
  if (!sec) return null;
  const pars = parsePar(sec.parData);
  const holeNum = holeIdx + 1;
  const par = pars[holeIdx] || 0;
  const players = editScores.filter(s => s.joinYn !== 'N');

  const handleReset = () => {
    setEditScores(prev => prev.map(s => {
      if (s.joinYn === 'N') return s;
      let json = safeParseJson(s.holeScoresJson);
      const sk = `no${secIdx + 1}`;
      const pk = `putting${secIdx + 1}`;
      if (!json[sk]) json[sk] = {};
      if (!json[pk]) json[pk] = {};
      json[sk][String(holeNum)] = '';
      json[pk][String(holeNum)] = '';
      return { ...s, holeScoresJson: json };
    }));
  };

  const safeParseJson = (val) => {
    if (!val) return {};
    try {
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      return parsed || {};
    } catch { return {}; }
  };

  const updateScore = (slotIdx, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const s = { ...next[slotIdx] };
      let json = safeParseJson(s.holeScoresJson);
      const key = `no${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(holeNum)]: value };
      s.holeScoresJson = json;
      next[slotIdx] = s;
      return next;
    });
  };

  const updatePutt = (slotIdx, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const s = { ...next[slotIdx] };
      let json = safeParseJson(s.holeScoresJson);
      const key = `putting${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(holeNum)]: value };
      s.holeScoresJson = json;
      next[slotIdx] = s;
      return next;
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 400, maxHeight: '70vh', overflow: 'auto', padding: '16px 16px 20px' }}>
        {/* 헤더 */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span style={{ fontSize: 16, fontWeight: 700 }}>{sec.sectionName} {holeNum}홀</span>
            <span className="ms-2" style={{ fontSize: 13, color: '#888' }}>PAR {par}</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={handleReset}>
              <IconRefresh size={14} /> 리셋
            </button>
            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={onSaveHole}>
              <IconDeviceFloppy size={14} /> 저장
            </button>
            <button className="btn btn-sm btn-ghost-secondary" onClick={onClose}>
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* 컬럼 헤더 */}
        <div className="d-flex justify-content-end mb-1" style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>
          <div className="d-flex align-items-center gap-3">
            <span style={{ width: 86, textAlign: 'center' }}>점수</span>
            <span style={{ width: 86, textAlign: 'center' }}>퍼팅</span>
          </div>
        </div>

        {/* 참가자별 */}
        {players.map(s => {
          const slotIdx = editScores.indexOf(s);
          const mem = members.find(m => m.id === Number(s.userId));
          const name = mem?.nickname || s.playerName || `P${s.playerSlot}`;
          const hd = parseHoles(s.holeScoresJson, `no${secIdx + 1}`);
          const pd = parseHoles(s.holeScoresJson, `putting${secIdx + 1}`);
          const scoreVal = hd[String(holeNum)];
          const puttVal = pd[String(holeNum)];
          const isScoreReset = scoreVal === '';
          const scoreNum = scoreVal != null && scoreVal !== '' ? Number(scoreVal) : 0;
          const isPuttReset = puttVal === '';
          const puttNum = puttVal != null && puttVal !== '' ? Number(puttVal) : 0;

          let scoreLabel = '';
          if (scoreNum <= -2) scoreLabel = 'Eagle';
          else if (scoreNum === -1) scoreLabel = 'Birdie';
          else if (scoreNum === 1) scoreLabel = 'Bogey';
          else if (scoreNum >= 2) scoreLabel = `+${scoreNum}`;

          return (
            <div key={s.playerSlot} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
              <div className="d-flex align-items-center gap-2">
                {mem?.profileImage ? (
                  <img src={mem.profileImage} alt={name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${mem.color || '#ddd'}` }} />
                ) : (
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: mem?.bgcolor || mem?.color || '#dee2e6',
                    color: mem?.bgcolor ? (mem?.color || '#fff') : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, border: `2px solid ${mem?.color || '#ccc'}`,
                  }}>
                    {name[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                  {scoreLabel && <div style={{ fontSize: 10, color: scoreNum < 0 ? '#dd4b39' : '#337ab7' }}>{scoreLabel}</div>}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                {/* 점수 */}
                <div className="d-flex align-items-center gap-1">
                  <button className="btn btn-sm btn-outline-secondary rounded-circle p-0"
                    style={{ width: 28, height: 28, fontSize: 16, lineHeight: 1 }}
                    onClick={() => updateScore(slotIdx, String(scoreNum - 1))}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 700, minWidth: 28, textAlign: 'center',
                    color: isScoreReset ? '#ccc' : scoreNum < 0 ? '#dd4b39' : scoreNum > 0 ? '#337ab7' : undefined
                  }}>{isScoreReset ? '-' : scoreNum}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-circle p-0"
                    style={{ width: 28, height: 28, fontSize: 16, lineHeight: 1 }}
                    onClick={() => updateScore(slotIdx, String(scoreNum + 1))}>+</button>
                </div>
                {/* 퍼팅 */}
                <div className="d-flex align-items-center gap-1">
                  <button className="btn btn-sm btn-outline-secondary rounded-circle p-0"
                    style={{ width: 28, height: 28, fontSize: 16, lineHeight: 1 }}
                    onClick={() => updatePutt(slotIdx, String(Math.max(0, puttNum - 1)))}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 700, minWidth: 28, textAlign: 'center', color: isPuttReset ? '#ccc' : '#0ca678' }}>{isPuttReset ? '-' : puttNum}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-circle p-0"
                    style={{ width: 28, height: 28, fontSize: 16, lineHeight: 1 }}
                    onClick={() => updatePutt(slotIdx, String(puttNum + 1))}>+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 개인 전체홀 입력 레이어
function PlayerEditLayer({ playerEditModal, sections, editScores, setEditScores, members, onClose, onSavePlayer }) {
  const { slotIdx } = playerEditModal;
  const s = editScores[slotIdx];
  if (!s) return null;
  const mem = members.find(m => m.id === Number(s.userId));
  const name = mem?.nickname || s.playerName || `P${s.playerSlot}`;

  const safeJson = (val) => {
    if (!val) return {};
    try { const p = typeof val === 'string' ? JSON.parse(val) : val; return p || {}; } catch { return {}; }
  };

  const updateVal = (secIdx, holeNum, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const cur = { ...next[slotIdx] };
      let json = safeJson(cur.holeScoresJson);
      const key = `no${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(holeNum)]: value };
      cur.holeScoresJson = json;
      next[slotIdx] = cur;
      return next;
    });
  };

  const updatePutt = (secIdx, holeNum, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const cur = { ...next[slotIdx] };
      let json = safeJson(cur.holeScoresJson);
      const key = `putting${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(holeNum)]: value };
      cur.holeScoresJson = json;
      next[slotIdx] = cur;
      return next;
    });
  };

  const updateField = (field, value) => {
    setEditScores(prev => {
      const next = [...prev];
      next[slotIdx] = { ...next[slotIdx], [field]: value };
      return next;
    });
  };

  const handleReset = () => {
    setEditScores(prev => {
      const next = [...prev];
      const cur = { ...next[slotIdx] };
      let json = safeJson(cur.holeScoresJson);
      sections.forEach((sec, si) => {
        const sk = `no${si + 1}`;
        const pk = `putting${si + 1}`;
        if (!json[sk]) json[sk] = {};
        if (!json[pk]) json[pk] = {};
        for (let h = 1; h <= (sec.holesCount || 9); h++) {
          json[sk][String(h)] = '';
          json[pk][String(h)] = '';
        }
      });
      cur.holeScoresJson = json;
      cur._manualScore = null;
      cur._manualPutting = null;
      next[slotIdx] = cur;
      return next;
    });
  };

  const json = safeJson(s.holeScoresJson);

  // 홀별 합산 계산
  let calcTotalScore = 0, calcTotalPar = 0, calcTotalPutting = 0;
  sections.forEach((sec, si) => {
    const pars = parsePar(sec.parData);
    const hd = json[`no${si + 1}`] || {};
    const pd = json[`putting${si + 1}`] || {};
    for (let h = 1; h <= (sec.holesCount || 9); h++) {
      calcTotalPar += (pars[h - 1] || 0);
      calcTotalScore += Number(hd[String(h)] || 0);
      calcTotalPutting += Number(pd[String(h)] || 0);
    }
  });

  const inputStyle = { width: 40, textAlign: 'center', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13, padding: '2px 0' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 420, maxHeight: '80vh', overflow: 'auto', padding: '16px 16px 20px' }}>
        {/* 헤더 */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            {mem?.profileImage ? (
              <img src={mem.profileImage} alt={name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${mem.color || '#ddd'}` }} />
            ) : (
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: mem?.bgcolor || mem?.color || '#dee2e6',
                color: mem?.bgcolor ? (mem?.color || '#fff') : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700, border: `2px solid ${mem?.color || '#ccc'}`,
              }}>
                {name[0]?.toUpperCase() || '?'}
              </div>
            )}
            <span style={{ fontSize: 18, fontWeight: 700 }}>{name}</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={handleReset}>
              <IconRefresh size={14} /> 리셋
            </button>
            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={onSavePlayer}>
              <IconDeviceFloppy size={14} /> 저장
            </button>
            <button className="btn btn-sm btn-ghost-secondary" onClick={onClose}>
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* 코스별 홀 입력 */}
        {(() => {
          // 섹션별 홀 시작 오프셋 계산
          const secOffsets = [];
          let totalHoles = 0;
          sections.forEach(sec => { secOffsets.push(totalHoles); totalHoles += (sec.holesCount || 9); });
          return sections.map((sec, secIdx) => {
          const pars = parsePar(sec.parData);
          const holes = Array.from({ length: sec.holesCount || 9 }, (_, i) => i + 1);
          const hd = json[`no${secIdx + 1}`] || {};
          const pd = json[`putting${secIdx + 1}`] || {};
          const parSum = pars.reduce((a, b) => a + b, 0);
          const secScoreSum = holes.reduce((a, h) => a + Number(hd[String(h)] || 0), 0);
          const secPuttSum = holes.reduce((a, h) => a + Number(pd[String(h)] || 0), 0);

          return (
            <div key={secIdx} className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <span style={{ fontSize: 13, fontWeight: 700, color: '#2471a3' }}>{sec.sectionName}</span>
                <span style={{ fontSize: 11, color: '#888' }}>
                  {parSum + secScoreSum}타 (+{secScoreSum}) P{secPuttSum}
                </span>
              </div>
              <table className="table table-sm table-bordered mb-0 text-center" style={{ fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f0f4f8' }}>
                    <th style={{ width: 30, fontSize: 10 }}>홀</th>
                    {holes.map(h => <th key={h} style={{ padding: '2px 0' }}>{h}</th>)}
                  </tr>
                  <tr style={{ background: '#f8f9fa' }}>
                    <td style={{ fontSize: 10, fontWeight: 600, color: '#888' }}>PAR</td>
                    {holes.map((h, i) => <td key={h} style={{ color: '#888', padding: '1px 0' }}>{pars[i] ?? '-'}</td>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ fontSize: 10, fontWeight: 600 }}>점수</td>
                    {holes.map((h, hi) => {
                      const v = hd[String(h)];
                      const num = v != null && v !== '' ? Number(v) : '';
                      const tabIdx = secOffsets[secIdx] + hi + 1;
                      return (
                        <td key={h} style={{ padding: '1px 0' }}>
                          <input type="number" value={num}
                            tabIndex={tabIdx}
                            className={`ple-tab${tabIdx}`}
                            onChange={e => updateVal(secIdx, h, e.target.value)}
                            onKeyDown={e => {
                              const key = e.key;
                              if (['0','1','2','3','4','5','6','7','8','9','-'].includes(key)) {
                                e.preventDefault();
                                if (key === '-') {
                                  const cur = e.target.value;
                                  if (cur && cur.startsWith('-')) return;
                                  updateVal(secIdx, h, '-' + (cur || ''));
                                  return;
                                }
                                const newVal = key === '9' ? '-1' : key;
                                updateVal(secIdx, h, newVal);
                                const nextEl = document.querySelector(`.ple-tab${tabIdx + 1}`);
                                if (nextEl) nextEl.focus();
                              }
                            }}
                            onFocus={e => e.target.select()}
                            style={{ width: '100%', maxWidth: 30, textAlign: 'center', border: '1px solid #eee', borderRadius: 3, fontSize: 12, padding: '1px 0' }} />
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td style={{ fontSize: 10, fontWeight: 600, color: '#888' }}>퍼팅</td>
                    {holes.map((h, hi) => {
                      const v = pd[String(h)];
                      const num = v != null && v !== '' ? Number(v) : '';
                      const tabIdx = totalHoles + secOffsets[secIdx] + hi + 1;
                      return (
                        <td key={h} style={{ padding: '1px 0' }}>
                          <input type="number" value={num}
                            tabIndex={tabIdx}
                            className={`ple-tab${tabIdx}`}
                            onChange={e => updatePutt(secIdx, h, e.target.value)}
                            onKeyDown={e => {
                              const key = e.key;
                              if (['0','1','2','3','4','5','6','7','8','9'].includes(key)) {
                                e.preventDefault();
                                updatePutt(secIdx, h, key);
                                const nextEl = document.querySelector(`.ple-tab${tabIdx + 1}`);
                                if (nextEl) nextEl.focus();
                              }
                            }}
                            onFocus={e => e.target.select()}
                            style={{ width: '100%', maxWidth: 30, textAlign: 'center', border: '1px solid #eee', borderRadius: 3, fontSize: 11, padding: '1px 0', color: '#888' }} />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        });
        })()}

        {/* 하단: Score / Handy / 퍼팅 */}
        <div className="border-top pt-2 mt-2">
          <div className="d-flex align-items-center justify-content-around gap-2">
            <div className="text-center">
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>Score</div>
              <input type="number" value={s._manualScore != null ? s._manualScore : ((calcTotalPar + calcTotalScore) || '')}
                onChange={e => updateField('_manualScore', e.target.value !== '' ? Number(e.target.value) : null)}
                style={inputStyle} />
            </div>
            <div className="text-center">
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>Handy</div>
              <input type="number" value={s.money ?? ''}
                onChange={e => updateField('money', e.target.value !== '' ? Number(e.target.value) : null)}
                style={inputStyle} />
            </div>
            <div className="text-center">
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>퍼팅</div>
              <input type="number" value={s._manualPutting != null ? s._manualPutting : (calcTotalPutting || '')}
                onChange={e => updateField('_manualPutting', e.target.value !== '' ? Number(e.target.value) : null)}
                style={inputStyle} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
